import { Component } from '@theme/component';
import { NavigationClickEvent, NavigationOpenEvent } from '@theme/events';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

const ACCORDION_ANIMATION_RESTORE_DELAY_MS = 100;
const STICKY_UNFREEZE_DELAY_MS = 180;

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #lockedScrollY = 0;
  #hasPendingOpenScroll = false;
  #summaryListenersOptions = { passive: true };

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onNavLinkClick);
    this.refs.details?.addEventListener('toggle', this.#handleRootToggle);
    this.addEventListener('toggle', this.#handleCategoryToggle, true);
    this.#bindSummaryListeners();
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onNavLinkClick);
    this.refs.details?.removeEventListener('toggle', this.#handleRootToggle);
    this.removeEventListener('toggle', this.#handleCategoryToggle, true);
    this.#unbindSummaryListeners();
    this.#unlockBodyScroll();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /** Fires navigation_open only when a top-level drawer category (Women, Men, …) opens. */
  #handleCategoryToggle = (event) => {
    const details = event.target;
    if (
      !(details instanceof HTMLDetailsElement) ||
      !details.open ||
      !details.matches('.menu-drawer__navigation > .menu-drawer__menu > li > details')
    ) return;
    const label = (details.querySelector(':scope > summary .menu-drawer__menu-item-text')?.textContent ?? '').trim();
    if (label) document.dispatchEvent(new NavigationOpenEvent(label));
  };

  #onNavLinkClick = (event) => {
    const nav = '.menu-drawer__navigation';
    const link = event.target.closest(`${nav} a[href]`);
    if (link) {
      const label = this.#linkLabel(link);
      if (!label) return;
      this.#dispatchNavigationClick(...this.#linkNavigationCategories(link, label));
      return;
    }

    const details = event.target
      .closest(`${nav} details.menu-drawer__menu-container > summary`)
      ?.closest('details.menu-drawer__menu-container');
    if (!details || details.matches(`${nav} > .menu-drawer__menu > li > details`)) return;

    const subLabel = this.#summaryLabel(details);
    if (!subLabel) return;

    const mainLabel = this.#summaryLabel(
      details.parentElement?.closest('details.menu-drawer__menu-container') ?? null
    );
    this.#dispatchNavigationClick(mainLabel || subLabel, mainLabel ? subLabel : undefined);
  };

  #linkNavigationCategories(link, label) {
    const inner = link.closest('details.menu-drawer__menu-container');
    const outer = inner?.parentElement?.closest('details.menu-drawer__menu-container');
    const innerLabel = inner ? this.#summaryLabel(inner) : '';
    const outerLabel = outer ? this.#summaryLabel(outer) : '';
    const depth3 =
      outerLabel &&
      (link.closest('.menu-drawer__menu--grandchildlist') ||
        link.classList.contains('menu-drawer__section-view-all'));

    if (depth3) return [outerLabel, innerLabel, label];
    if (innerLabel) return [innerLabel, label];
    return [label];
  }

  #dispatchNavigationClick(main, sub, subsub) {
    document.dispatchEvent(new NavigationClickEvent(main, sub, subsub));
  }

  #summaryLabel(details) {
    if (!details) return '';
    return (details.querySelector(':scope > summary .menu-drawer__menu-item-text')?.textContent ?? '').trim();
  }

  #linkLabel(link) {
    return (link.querySelector('.menu-drawer__menu-item-text')?.textContent ?? link.textContent ?? '').trim();
  }

  #handleRootToggle = () => {
    if (!this.refs.details) return;

    if (this.refs.details.hasAttribute('open')) {
      this.#lockBodyScroll();
    } else {
      this.#unlockBodyScroll();
    }
  };

  #captureOpenScroll = () => {
    if (!this.refs.details || this.refs.details.hasAttribute('open')) return;
    this.#lockedScrollY = window.scrollY || window.pageYOffset || 0;
    this.#hasPendingOpenScroll = true;
  };

  #getSummary(details = this.refs.details) {
    return details?.querySelector('summary') ?? null;
  }

  #setExpanded(details, isExpanded) {
    this.#getSummary(details)?.setAttribute('aria-expanded', String(isExpanded));
  }

  #getAnimatedDrawer(details) {
    return details.querySelector('.menu-drawer, .menu-drawer__submenu');
  }

  #bindSummaryListeners() {
    const summary = this.#getSummary();
    if (!summary) return;

    summary.addEventListener('pointerdown', this.#captureOpenScroll, this.#summaryListenersOptions);
    summary.addEventListener('touchstart', this.#captureOpenScroll, this.#summaryListenersOptions);
    summary.addEventListener('click', this.#captureOpenScroll);
  }

  #unbindSummaryListeners() {
    const summary = this.#getSummary();
    if (!summary) return;

    summary.removeEventListener('pointerdown', this.#captureOpenScroll);
    summary.removeEventListener('touchstart', this.#captureOpenScroll);
    summary.removeEventListener('click', this.#captureOpenScroll);
  }

  #resetDetails(details) {
    details.classList.remove('menu-open');
    details.removeAttribute('open');
    this.#setExpanded(details, false);
  }

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = this.#getSummary(details);

    if (!summary) return;

    this.#setExpanded(details, true);

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
        details.querySelector('localization-form-component')?.dispatchCountryPickerOpen?.();
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = this.#getAnimatedDrawer(details);
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = this.#getSummary(details);

    if (!summary) return;

    this.#setExpanded(details, false);
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = this.#getAnimatedDrawer(details);

    onAnimationEnd(
      drawer || details,
      () => {
        this.#resetDetails(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach((openDetail) => this.#resetDetails(openDetail));
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, ACCORDION_ANIMATION_RESTORE_DELAY_MS);
  }

  #lockBodyScroll() {
    if (document.body.dataset.mobileMenuScrollLock === 'true') return;
    const { body } = document;

    if (!this.#hasPendingOpenScroll) {
      this.#lockedScrollY = window.scrollY || window.pageYOffset || 0;
    }
    this.#hasPendingOpenScroll = false;
    body.dataset.mobileMenuScrollLock = 'true';
    body.style.position = 'fixed';
    body.style.top = `-${this.#lockedScrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';

    const headerComponent = document.querySelector('header-component');
    if (headerComponent instanceof HTMLElement) {
      headerComponent.dataset.freezeStickyState = 'true';
    }
  }

  #unlockBodyScroll() {
    if (document.body.dataset.mobileMenuScrollLock !== 'true') return;

    const { body, documentElement: root } = document;
    const previousRootScrollBehavior = root.style.scrollBehavior;
    const previousBodyScrollBehavior = body.style.scrollBehavior;
    const lockedTop = Number.parseInt(body.style.top || '0', 10) || 0;
    const restoreY = Math.abs(lockedTop);

    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    delete body.dataset.mobileMenuScrollLock;

    // Temporarily disable smooth behavior to avoid animated restore.
    root.style.scrollBehavior = 'auto';
    body.style.scrollBehavior = 'auto';
    window.scrollTo(0, restoreY);

    requestAnimationFrame(() => {
      root.style.scrollBehavior = previousRootScrollBehavior;
      body.style.scrollBehavior = previousBodyScrollBehavior;

      // Keep sticky calculations frozen until the restore scroll settles.
      window.setTimeout(() => {
        const headerComponent = document.querySelector('header-component');
        if (headerComponent instanceof HTMLElement) {
          delete headerComponent.dataset.freezeStickyState;
        }
      }, STICKY_UNFREEZE_DELAY_MS);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}
